import React, { useMemo } from 'react';
import classNames from 'classnames';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { Spinner } from 'js/src/components/common/ui/Spinner';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { isUploadedAudio, isUploadedMedia } from 'js/src/components/sketch/FileUpload';

interface ChatFileListProps {
    files: UploadedFile[];
    onRemoveFile?: (fileId: string) => void;
    showRemoveButton?: boolean;
    showFileName?: boolean;
    className?: string;
}

export const ChatFileList: React.FC<ChatFileListProps> = ({
    files,
    onRemoveFile,
    showRemoveButton = true,
    showFileName = true,
    className = '',
}) => {
    const mediaRegistry = useMemo(() => MediaRegistry.getInstance(), []);
    if (files.length === 0) {
        return null;
    }

    return (
        <div className={classNames('chat-file-list', className)}>
            {files.map((file) => {
                const isAudio = isUploadedAudio(file);
                const isMedia = isUploadedMedia(file);
                // Audio with a voiceover is a voice track (mic); audio without one is a
                // music track (music note).
                const audioIcon = isAudio && file.has_voiceover ? 'mic' : 'music_note';
                const icon = isAudio
                    ? audioIcon
                    : isMedia
                      ? file.type.startsWith('image/')
                          ? 'image'
                          : 'movie'
                      : 'article';

                let previewUrl = isMedia ? file.local_preview_url : undefined;
                if (isMedia && file.mediaRegistryId) {
                    const mediaItem = mediaRegistry.getItem(file.mediaRegistryId);
                    if (mediaItem?.background) {
                        if (file.type.startsWith('video/')) {
                            previewUrl = mediaItem.background.video?.preview_url;
                        } else {
                            previewUrl = mediaItem.background.image?.preview_url;
                        }
                    }
                }

                return (
                    <Tooltip key={file.id} title={file.error}>
                        <div className="chat-file-item d-flex align-items-center">
                            <div className="d-flex align-items-center flex-grow-1 min-width-0">
                                {isMedia && previewUrl ? (
                                    <div className="chat-file-preview">
                                        {file.type.startsWith('video/') ? (
                                            <video
                                                src={previewUrl}
                                                className="chat-file-preview-media"
                                                muted
                                            />
                                        ) : (
                                            <img
                                                src={previewUrl}
                                                alt={file.name}
                                                className="chat-file-preview-media"
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <i translate="no" className="material-symbols-outlined me-l5-8">
                                        {icon}
                                    </i>
                                )}
                                {((showFileName && file.name) || file.error) && (
                                    <div className="min-width-0 flex-grow-1 ms-l5-8">
                                        {showFileName && (
                                            <div className="document-name">{file.name}</div>
                                        )}
                                        {file.error && (
                                            <div className="text-muted small">
                                                <span className="text-danger">Error</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="d-flex align-items-center ms-l5-8">
                                {file.isUploading && <Spinner size="sm" className="me-l5-8" />}
                                {showRemoveButton && onRemoveFile && (
                                    <button
                                        type="button"
                                        className="btn-close btn-close-sm cursor-pointer"
                                        aria-label={
                                            file.isUploading ? 'Cancel upload' : 'Remove file'
                                        }
                                        onClick={() => onRemoveFile(file.id)}
                                    />
                                )}
                            </div>
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
};
